import { useEffect, useRef, useState } from 'react';
import { useApp } from '../context';
import type { PlexHub } from '../plex';
import { linkProps, navigate } from '../router';
import { Row } from '../components/Card';
import { call, iptv, seerrApi, type SeerrMedia } from '../api';
import { ChannelLogo, ChannelName, LiveBadge, clock, dayLabel, playLink } from '../components/LiveBits';
import { RequestCard } from '../components/RequestCard';
import { WidgetView } from '../components/Widgets';
import { addWidgetToHome } from '../layout';
import type { WidgetSource } from '../api';

const HUB_TITLES: Record<string, string> = {
  movie: 'Films',
  show: 'Series',
  episode: 'Afleveringen',
};

interface PersonHit { id: number; name: string; photo: string | null; knownFor: string | null }
interface LiveHit {
  channels: { accountId: string; streamId: number; name: string; logo: string | null }[];
  programmes: { title: string; subTitle?: string; start: number; stop: number; live: boolean; channel: { accountId: string; streamId: number; name: string; logo: string | null; catchup: boolean } }[];
}

/** Eén zoekbalk voor alles: bibliotheek, personen, Live TV (zenders en programma's) en aanvragen. */
export function Search({ initial }: { initial: string }) {
  const { plex } = useApp();
  const [query, setQuery] = useState(initial);
  const [hubs, setHubs] = useState<PlexHub[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const [seerr, setSeerr] = useState<{ configured: boolean; canRequest: boolean } | null>(null);
  const [missing, setMissing] = useState<SeerrMedia[]>([]);
  const [people, setPeople] = useState<PersonHit[]>([]);
  const [live, setLive] = useState<LiveHit | null>(null);
  const [hasIptv, setHasIptv] = useState(false);
  const [recent, setRecent] = useState<string[]>([]);
  // Slim zoeken: "Nederlandse films uit de jaren 90 onder 2 uur"
  const [smart, setSmart] = useState<{ chips: string[]; total: number; widget: WidgetSource; q: string } | null>(null);
  const [smartAdded, setSmartAdded] = useState(false);
  useEffect(() => {
    const q = query.trim();
    setSmartAdded(false);
    if (q.length < 6 || q.split(/\s+/).length < 2) {
      setSmart(null);
      return;
    }
    const t = window.setTimeout(() => {
      call<{ smart: boolean; chips?: string[]; total?: number; widget?: WidgetSource }>(`/api/search/smart?q=${encodeURIComponent(q)}`)
        .then((r) => setSmart(r.smart && r.widget ? { chips: r.chips ?? [], total: r.total ?? 0, widget: r.widget, q } : null))
        .catch(() => setSmart(null));
    }, 450);
    return () => window.clearTimeout(t);
  }, [query]);

  useEffect(() => {
    call<{ recent: string[] }>('/api/search/recent').then((r) => setRecent(r.recent)).catch(() => setRecent([]));
  }, []);

  /** Onthouden zodra je iets uit de resultaten opent (of op Enter drukt). */
  const remember = () => {
    const q = query.trim();
    if (q.length < 2 || recent[0]?.toLowerCase() === q.toLowerCase()) return;
    call<{ recent: string[] }>('/api/search/recent', { method: 'POST', body: JSON.stringify({ q }) }).then((r) => setRecent(r.recent)).catch(() => {});
  };
  const forget = (q?: string) => {
    call<{ recent: string[] }>(`/api/search/recent${q ? `?q=${encodeURIComponent(q)}` : ''}`, { method: 'DELETE' }).then((r) => setRecent(r.recent)).catch(() => {});
  };

  useEffect(() => {
    iptv.accounts().then((r) => setHasIptv(r.accounts.some((a) => a.enabledForProfile))).catch(() => setHasIptv(false));
  }, []);

  // Personen en Live TV, iets later dan de bibliotheek (die is het belangrijkst)
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setPeople([]);
      setLive(null);
      return;
    }
    const t = window.setTimeout(() => {
      call<{ people: PersonHit[] }>(`/api/search/people?q=${encodeURIComponent(q)}`).then((r) => setPeople(r.people)).catch(() => setPeople([]));
      if (hasIptv) call<LiveHit>(`/api/iptv/search?q=${encodeURIComponent(q)}`).then(setLive).catch(() => setLive(null));
    }, 450);
    return () => window.clearTimeout(t);
  }, [query, hasIptv]);

  useEffect(() => {
    seerrApi.status().then(setSeerr).catch(() => setSeerr(null));
  }, []);

  // Wat niet in de bibliotheek staat: direct aanvragen via Seerr
  useEffect(() => {
    const q = query.trim();
    if (!seerr?.configured || q.length < 2) {
      setMissing([]);
      return;
    }
    const t = window.setTimeout(() => {
      seerrApi.search(q).then((r) => setMissing(r.results.filter((m) => m.status !== 'available').slice(0, 12))).catch(() => setMissing([]));
    }, 400);
    return () => window.clearTimeout(t);
  }, [query, seerr?.configured]);

  useEffect(() => {
    input.current?.focus();
  }, []);

  useEffect(() => {
    const q = query.trim();
    // URL bijwerken zodat terug-navigeren de zoekopdracht bewaart
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search', { replace: true });
    if (q.length < 2) {
      setHubs(null);
      return;
    }
    const t = window.setTimeout(() => {
      plex.search(q)
        .then((h) => {
          setHubs(h.filter((x) => x.type in HUB_TITLES));
          setError(null);
        })
        .catch(() => setError('Zoeken mislukt.'));
    }, 300);
    return () => window.clearTimeout(t);
  }, [plex, query]);

  return (
    <main className="page" onClickCapture={(e) => { if ((e.target as HTMLElement).closest('a') && !(e.target as HTMLElement).closest('.search-recent')) remember(); }}>
      <form className="search-form" role="search" onSubmit={(e) => { e.preventDefault(); remember(); }}>
        <label htmlFor="q" className="visually-hidden">Zoeken</label>
        <input
          id="q"
          ref={input}
          className="search-input"
          type="search"
          placeholder="Zoek films, series… of probeer: Nederlandse films uit de jaren 90 onder 2 uur"
          autoComplete="off"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </form>
      {query.trim().length < 2 && recent.length > 0 && (
        <section className="search-recent" aria-label="Recente zoekopdrachten">
          <h2>
            Recent gezocht
            <button type="button" className="row-more linklike" onClick={() => forget()}>Wissen</button>
          </h2>
          <div className="filterbar" data-focus-group>
            {recent.map((q) => (
              <span key={q} className="chip chip-recent">
                <button type="button" onClick={() => { setQuery(q); input.current?.focus(); }}>↺ {q}</button>
                <button type="button" className="chip-x" aria-label={`${q} weghalen`} onClick={() => forget(q)}>✕</button>
              </span>
            ))}
          </div>
        </section>
      )}
      {error && <p className="placeholder-row error" role="alert">{error}</p>}
      {smart && (
        <section className="smart-search" aria-label="Slim zoeken">
          <div className="smart-head">
            <span className="subtle">Zo begrepen:</span>
            {smart.chips.map((c) => <span key={c} className="chip chip-static">{c}</span>)}
            <span className="subtle">{smart.total.toLocaleString('nl-NL')} titels</span>
            <button className="btn btn-sm" disabled={smartAdded} onClick={() => void addWidgetToHome({ title: smart.q.charAt(0).toUpperCase() + smart.q.slice(1), source: smart.widget, view: 'poster', show: 'all', limit: 20, enabled: true }).then(() => setSmartAdded(true))}>
              {smartAdded ? '✓ Op Home' : '+ Zet op Home'}
            </button>
          </div>
          <WidgetView key={JSON.stringify(smart.widget)} w={{ id: 'smart', title: 'Gevonden', source: smart.widget, view: 'poster', show: 'all', limit: 30, enabled: true }} />
        </section>
      )}
      {hubs?.length === 0 && !smart && <p className="placeholder-row">Niets gevonden voor “{query.trim()}”.</p>}
      {hubs?.map((h) => (
        <Row key={h.hubIdentifier} title={HUB_TITLES[h.type] ?? h.title} items={h.Metadata ?? []} kind={h.type === 'episode' ? 'landscape' : 'poster'} />
      ))}
      {people.length > 0 && (
        <section className="row" aria-label="Personen">
          <h2>Personen</h2>
          <div className="row-track" data-focus-group>
            {people.map((p) => (
              <a key={p.id} className="cast" {...linkProps(`/person/${p.id}`)} aria-label={`${p.name}${p.knownFor ? `, bekend van ${p.knownFor}` : ''}`}>
                <span className="cast-photo" style={p.photo ? { backgroundImage: `url("${p.photo}")` } : undefined} aria-hidden="true">{!p.photo && p.name.slice(0, 1)}</span>
                <span className="card-title">{p.name}</span>
                {p.knownFor && <span className="card-sub">{p.knownFor}</span>}
              </a>
            ))}
          </div>
        </section>
      )}

      {live && (live.channels.length > 0 || live.programmes.length > 0) && (
        <section className="row" aria-label="Live TV">
          <h2>Live TV</h2>
          {live.channels.length > 0 && (
            <div className="filterbar search-channels" data-focus-group>
              {live.channels.map((c) => (
                <a key={`${c.accountId}:${c.streamId}`} className="chip chip-channel" {...linkProps(playLink(c.accountId, c.streamId))}>
                  <ChannelLogo src={c.logo} name={c.name} size={22} />
                  <ChannelName name={c.name} />
                </a>
              ))}
            </div>
          )}
          {live.programmes.length > 0 && (
            <div className="search-progs" data-focus-group>
              {live.programmes.map((p) => {
                const past = p.stop < Date.now();
                // Afgelopen: terugkijken; live of later: naar de zender
                const href = past ? playLink(p.channel.accountId, p.channel.streamId, { start: p.start, end: p.stop }) : playLink(p.channel.accountId, p.channel.streamId);
                return (
                  <a key={`${p.channel.streamId}:${p.start}`} className="search-prog" {...linkProps(href)}>
                    <span className="search-prog-time">
                      {p.live ? <LiveBadge /> : <span>{dayLabel(p.start)} {clock(p.start)}</span>}
                      {past && <span className="subtle"> · terugkijken</span>}
                    </span>
                    <strong>{p.title}</strong>
                    {p.subTitle && <span className="subtle clamp-2">{p.subTitle}</span>}
                    <span className="search-prog-ch"><ChannelLogo src={p.channel.logo} name={p.channel.name} size={20} /><ChannelName name={p.channel.name} /></span>
                  </a>
                );
              })}
            </div>
          )}
        </section>
      )}

      {missing.length > 0 && (
        <section className="row" aria-label="Niet in je bibliotheek">
          <h2>
            Niet in je bibliotheek
            <a className="row-more" {...linkProps(`/requests?q=${encodeURIComponent(query.trim())}`)}>Meer in Verzoeken</a>
          </h2>
          <div className="row-track" data-focus-group>
            {missing.map((m) => <div key={`${m.mediaType}-${m.tmdbId}`} className="row-req"><RequestCard m={m} canRequest={seerr?.canRequest ?? false} /></div>)}
          </div>
        </section>
      )}
    </main>
  );
}
